// Report generation — opened from the "Report" column in IpdRadiologyOrders.js.
//
// There are NO report fields here, and none anywhere else in the frontend. The
// radiologist picks a template, its content is copied into a rich text editor,
// and whatever they type is the report. Structure — headings, tables, organ
// lists, letterhead — lives entirely in the template the hospital authored.
//
// Flow: select template → content copied (with {{placeholders}} resolved) →
// edit freely → Save draft / Finalize. From the moment a template is applied,
// the report owns its text; later template edits never touch it
// (see useRadiologyReport.applyTemplate).
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import ClinicalEditor from "../../components/ClinicalEditor/ClinicalEditor";
import { C, getAuthHeaders } from "../ipd-billing/constants";
import { Btn, Modal, cardStyle, inputStyle, labelStyle } from "../rule-builder/ui";
import SvgIcon from "../ipd-billing/SvgIcon";
import { formatDateTime, withSalutation } from "./radiologyOrdersShared";
import useRadiologyReport from "./hooks/useRadiologyReport";
import useRadiologyTemplates, {
  suggestTemplate,
  templatesForOrder,
} from "./hooks/useRadiologyTemplates";
import { htmlIsEmpty, unresolvedPlaceholders } from "./utils/radiologyPlaceholders";
import { fetchTemplateFileHtml } from "./api/radiologyReportApi";
import { printRadiologyReport } from "./utils/printRadiologyReport";

function MetaChip({ label, value }) {
  return (
    <div style={{ ...cardStyle, padding: "7px 12px", flex: "1 1 130px" }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: C.textSub,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>
        {value || "-"}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const final = status === "final";
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        padding: "3px 9px",
        borderRadius: 20,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        background: final ? "#F0FDFA" : "#FFFBEB",
        color: final ? "#0F766E" : "#B45309",
      }}
    >
      {final ? "Finalized" : "Draft"}
    </span>
  );
}

const ABDM_FEATURES = [
  { icon: "shield", label: "Secure & Verified" },
  { icon: "check", label: "ABDM Compliant" },
  { icon: "users", label: "Patient Controlled" },
  { icon: "sync", label: "Instant Access" },
];

export default function RadiologyReportModal({ patient, order, report: initialReport, onSave, onClose }) {
  // The investigation this study was ordered for. When it's known the template
  // list is fetched already scoped to it (get_radiology_report_templates/), so
  // an MRI Brain report can only ever be offered MRI Brain layouts. Orders that
  // predate the field fall back to the whole list, narrowed by modality below.
  const medicalServiceId = order?.medicalServiceId || "";
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    reload: reloadTemplates,
  } = useRadiologyTemplates({
    activeOnly: true,
    medicalServiceId: medicalServiceId || null,
  });
  const {
    report,
    saving,
    dirty,
    isFinal,
    isEmpty: blank,
    setContent,
    setRadiologist,
    applyTemplate,
    saveDraft,
    finalize,
    reopen,
  } = useRadiologyReport({ patient, order, initialReport });

  const [templateId, setTemplateId] = useState("");
  const [autoSuggested, setAutoSuggested] = useState(false);
  // Load Template now fetches the body before it can apply it — the list call
  // returns metadata only.
  const [applying, setApplying] = useState(false);
  const editorRef = useRef(null);

  // Supplies the facility's uploaded logo to the PDF letterhead; falls back to
  // the bundled one when a facility has no branding configured.
  const brandingData = useContext(BrandingContext);
  // Facility address for the letterhead, from get_logged_in_user_info/ — the
  // same source the billing invoice uses. Reading it here rather than from
  // sessionStorage matters: only the landing screens write those keys, so a
  // radiologist who logs straight into this workspace has none of them.
  const { userInfo } = usePermissions();

  // Templates this order may be reported with. With a medical_service_id the
  // backend has already narrowed the list to that investigation, so filtering
  // again on modality here would only risk hiding valid layouts (uploaded
  // templates carry no modality). Without one, fall back to the client-side
  // modality narrowing so an MRI order still can't load a CT layout.
  const availableTemplates = useMemo(
    () =>
      medicalServiceId
        ? templates
        : templatesForOrder(templates, order, report.template_id),
    [medicalServiceId, templates, order, report.template_id],
  );

  const suggestion = useMemo(
    () => suggestTemplate(availableTemplates, order),
    [availableTemplates, order],
  );

  // Preselect the best-matching template for a blank report, once. An existing
  // report keeps whatever it was written from — reselecting would invite an
  // accidental overwrite of finalized text.
  useEffect(() => {
    if (autoSuggested || templatesLoading) return;
    if (report.template_id) {
      setTemplateId(report.template_id);
    } else if (suggestion) {
      setTemplateId(suggestion.template_id);
    } else if (availableTemplates.length === 1) {
      // A service-scoped list carries no service_names to score on, so
      // suggestTemplate usually abstains. One candidate needs no scoring.
      setTemplateId(availableTemplates[0].template_id);
    }
    setAutoSuggested(true);
  }, [autoSuggested, templatesLoading, suggestion, report.template_id, availableTemplates]);

  const selectedTemplate = availableTemplates.find((t) => t.template_id === templateId);
  const pending = unresolvedPlaceholders(report.content_html);

  const handleApply = async () => {
    if (!selectedTemplate) {
      toast.error("Select a template first.");
      return;
    }
    if (!blank && !window.confirm("Replace the current report content with this template?")) {
      return;
    }
    setApplying(true);
    try {
      // A template listed by get_radiology_report_templates/ carries no body —
      // pull the uploaded file and decode it. Templates that already hold their
      // HTML (the local authoring index) skip the round trip.
      let html = selectedTemplate.content_html;
      if (htmlIsEmpty(html)) {
        html = await fetchTemplateFileHtml({
          templateId: selectedTemplate.template_id,
          medicalServiceId: selectedTemplate.medical_service_id || medicalServiceId,
        });
      }
      if (htmlIsEmpty(html)) {
        toast.error("This template has no content to load.");
        return;
      }
      // applyTemplate resolves {{placeholders}} and takes the report's own copy
      // of the text — the template is never read again after this point.
      const hydrated = applyTemplate({ ...selectedTemplate, content_html: html });
      // Push into the editor directly — see applyTemplate's note on focus.
      editorRef.current?.getEditor?.()?.commands.setContent(hydrated, false);
      toast.success(`Template "${selectedTemplate.template_name}" applied.`);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Could not load the template.",
      );
    } finally {
      setApplying(false);
    }
  };

  const commit = async (finalizing) => {
    if (blank) {
      toast.error("The report is empty — apply a template or type the report first.");
      return;
    }
    if (finalizing && pending.length) {
      const ok = window.confirm(
        `This report still contains unfilled placeholders: ${pending
          .map((p) => `{{${p}}}`)
          .join(", ")}.\n\nFinalize anyway?`,
      );
      if (!ok) return;
    }
    try {
      const saved = finalizing ? await finalize() : await saveDraft();
      // Keeps the orders table/KPIs in step — finalizing flips the order to
      // "Reported" (radiologyOrdersShared.STATUS_META).
      if (onSave) onSave(saved.order_no || order?.orderNo, patient, saved);
      toast.success(finalizing ? "Report finalized." : "Draft saved.");
      if (finalizing) onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not save the report.");
    }
  };

  // ABDM linking is a user-initiated action inside the report itself — it
  // always starts unlinked here, and flips to "Linked" only once the 4-step
  // handshake below completes.
  const [abdmLinked, setAbdmLinked] = useState(false);
  const [abdmTransactionId, setAbdmTransactionId] = useState(null);
  const [abdmLinking, setAbdmLinking] = useState(false);

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const canLinkAbha =
    Boolean(patient?.abhaId) && Boolean(order?.careContextReferenceId);

  // Same 4-call ABDM handshake as LabReportModal's handleLinkAbha, chained the
  // same way (.then into the next call rather than awaiting each one): store
  // the pending care-context link, run the demographic auth init/confirm
  // round-trip against the patient's ABHA address, then attach the resulting
  // access token + care context to the patient's ABHA record.
  const handleLinkAbha = () => {
    if (!patient?.abhaId) {
      toast.error("Patient has no ABHA address on file");
      return;
    }
    if (!order?.careContextReferenceId) {
      toast.error("No care context reference available for this report yet");
      return;
    }

    const dob = new Date(patient.dob);
    if (Number.isNaN(dob.getTime())) {
      toast.error("Patient date of birth is missing or invalid");
      return;
    }
    const pad = (n) => String(n).padStart(2, "0");
    // ABDM expects YYYY-DD-MM here (matches the working demo integration's
    // formatDateOfBirth), not the usual YYYY-MM-DD.
    const dateOfBirth = `${dob.getFullYear()}-${pad(dob.getDate())}-${pad(dob.getMonth() + 1)}`;

    setAbdmLinking(true);

    const pendingForm = new FormData();
    pendingForm.append("patient_id", patient.patientId);
    pendingForm.append("careContextReferenceID", order.careContextReferenceId);

    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}save_pending_care_context_linking/`,
        pendingForm,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        },
      )
      .then((response) => {
        var post_data = {};
        post_data.query = {};

        post_data.query.id = patient.abhaId;
        post_data.query.purpose = "KYC_AND_LINK";
        post_data.query.requester = {
          type: "HIP",
          id: sessionStorage.getItem("facility_name"),
        };
        post_data.query.authMode = "DEMOGRAPHICS";

        axios
          .post(
            `${REACT_APP_BACKEND_ROUTE_URL}auth_init_workflow/`,
            JSON.stringify(post_data),
            { headers: getAuthHeaders() },
          )
          .then((res) => {
            if (res.data.status !== "" && res.data.status === "Success") {
              var post_data1 = {};
              post_data1.credential = {};

              post_data1.transactionId = res.data.transaction_id;
              post_data1.credential.demographic = {};
              post_data1.credential.demographic.name = patient.name;
              post_data1.credential.demographic.gender = patient.gender;
              post_data1.credential.demographic.dateOfBirth = dateOfBirth;
              post_data1.credential.demographic.authCode = "";

              axios
                .post(
                  `${REACT_APP_BACKEND_ROUTE_URL}auth_confirm_workflow/`,
                  JSON.stringify(post_data1),
                  { headers: getAuthHeaders() },
                )
                .then((res1) => {
                  if (
                    res1.data.status !== "" &&
                    res1.data.status === "Success"
                  ) {
                    var post_data2 = {};
                    post_data2.link = {};
                    post_data2.link.accessToken = res1.data.access_token;
                    post_data2.link.patient = {};
                    post_data2.link.patient.referenceNumber = patient.patientId;
                    post_data2.link.patient.display = patient.abhaId;
                    post_data2.link.patient.careContexts = [];
                    var careContextData = {};
                    careContextData.display =
                      order.careContextDisplay || order.service;
                    careContextData.referenceNumber =
                      order.careContextReferenceId;
                    post_data2.link.patient.careContexts[0] = careContextData;
                    post_data2.pending_care_context_transaction_id =
                      response.data.pending_care_context_transaction_id;

                    axios
                      .post(
                        `${REACT_APP_BACKEND_ROUTE_URL}add_context_workflow/`,
                        JSON.stringify(post_data2),
                        { headers: getAuthHeaders() },
                      )
                      .then((res2) => {
                        if (res2.data.status !== "") {
                          if (res2.data.status === "Success") {
                            setAbdmTransactionId(
                              response.data.pending_care_context_transaction_id,
                            );
                            setAbdmLinked(true);
                            toast.success(
                              `${patient.name}'s report linked to ABHA`,
                            );
                          } else {
                            toast.error("some error occurred");
                          }
                        }
                        setAbdmLinking(false);
                      })
                      .catch((err) => {
                        console.log(err);
                        toast.error(
                          err?.response?.data?.message || "ABHA linking failed",
                        );
                        setAbdmLinking(false);
                      });
                  } else {
                    toast.error("some error occurred");
                    setAbdmLinking(false);
                  }
                })
                .catch((err) => {
                  console.log(err);
                  toast.error(
                    err?.response?.data?.message || "ABHA verification failed",
                  );
                  setAbdmLinking(false);
                });
            } else {
              toast.error("some error occurred");
              setAbdmLinking(false);
            }
          })
          .catch((err) => {
            console.log(err);
            toast.error(
              err?.response?.data?.message ||
                "Could not initiate ABHA verification",
            );
            setAbdmLinking(false);
          });
      })
      .catch((error) => {
        console.error("There was an error!", error);
        toast.error(
          error?.response?.data?.message ||
            "Could not store pending care context link",
        );
        setAbdmLinking(false);
      });
  };

  const handleClose = () => {
    if (dirty && !window.confirm("Discard unsaved changes to this report?")) return;
    onClose();
  };

  return (
    <Modal
      open
      onClose={handleClose}
      width={1080}
      title={`Radiology Report — ${order?.service || ""}`}
      description={`${patient?.name || ""} · ${patient?.uhid || ""} · Order ${order?.orderNo || ""}`}
      footer={
        <>
          <div style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <StatusBadge status={report.status} />
            {report.template_name ? (
              <span style={{ fontSize: 11, color: C.textSub }}>
                From template: <strong style={{ color: C.text }}>{report.template_name}</strong>
              </span>
            ) : null}
            {dirty ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#B45309" }}>Unsaved</span>
            ) : null}
          </div>
          <Btn
            variant="outline"
            onClick={() =>
              printRadiologyReport({ patient, order, report, brandingData, userInfo })
            }
            disabled={blank}
          >
            <SvgIcon name="printer" size={14} color={C.text} />
            Print
          </Btn>
          {isFinal ? (
            <Btn variant="outline" onClick={reopen}>
              <SvgIcon name="edit" size={14} color={C.text} />
              Amend report
            </Btn>
          ) : (
            <>
              <Btn variant="outline" onClick={() => commit(false)} disabled={saving}>
                {saving ? "Saving…" : "Save Draft"}
              </Btn>
              <Btn onClick={() => commit(true)} disabled={saving || blank}>
                <SvgIcon name="check" size={14} color="#fff" />
                Finalize Report
              </Btn>
            </>
          )}
        </>
      }
    >
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <MetaChip label="Modality" value={order?.modality} />
        <MetaChip label="Priority" value={order?.priority} />
        <MetaChip label="Referred by" value={withSalutation(order?.doctor)} />
        <MetaChip label="Order date" value={formatDateTime(order?.ordered)} />
        <MetaChip
          label="Scheduled"
          value={order?.scheduled ? formatDateTime(order.scheduled) : "-"}
        />
      </div>

      <div
        style={{
          ...cardStyle,
          background: "#F8FAFC",
          padding: "12px 14px",
          marginBottom: 14,
          display: "flex",
          gap: 12,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "2 1 280px" }}>
          <label style={labelStyle}>
            Report template
            {medicalServiceId
              ? order?.service
                ? ` · ${order.service} only`
                : ""
              : order?.modality && order.modality !== "-"
                ? ` · ${order.modality} only`
                : ""}
          </label>
          <select
            style={inputStyle}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={isFinal || templatesLoading || !availableTemplates.length}
          >
            <option value="">
              {templatesLoading
                ? "Loading templates…"
                : availableTemplates.length
                  ? "Select a template…"
                  : "No templates available"}
            </option>
            {availableTemplates.map((t) => (
              <option key={t.template_id} value={t.template_id}>
                {t.template_name}
                {t.modality ? ` · ${t.modality}` : ""}
                {suggestion?.template_id === t.template_id ? "  (suggested)" : ""}
              </option>
            ))}
          </select>
        </div>
        {/* Wrapped so the button's own box, not its baseline, is what lines up
            with the select and the radiologist input on the flex-end row. */}
        <div style={{ display: "flex", flexShrink: 0 }}>
          <Btn
            variant="outline"
            onClick={handleApply}
            disabled={isFinal || !templateId || applying || templatesLoading}
          >
            <SvgIcon name="download" size={13} color={C.text} />
            {applying ? "Loading…" : "Load template"}
          </Btn>
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label style={labelStyle}>Reporting radiologist</label>
          <input
            style={inputStyle}
            value={report.radiologist_name}
            onChange={(e) => setRadiologist(e.target.value)}
            placeholder="Dr. …"
            disabled={isFinal}
          />
        </div>
      </div>

      {templatesError ? (
        <div
          style={{
            ...cardStyle,
            padding: "10px 14px",
            marginBottom: 12,
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            fontSize: 12,
            color: "#991B1B",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          {templatesError}
          <Btn variant="outline" size="sm" onClick={reloadTemplates}>
            Retry
          </Btn>
        </div>
      ) : null}

      {!templatesLoading && !templatesError && !availableTemplates.length ? (
        <div
          style={{
            ...cardStyle,
            padding: "10px 14px",
            marginBottom: 12,
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            fontSize: 12,
            color: "#92400E",
          }}
        >
          {medicalServiceId ? (
            <>
              No template exists for <strong>{order?.service || "this investigation"}</strong>{" "}
              yet. Author one under <strong>Report Templates</strong> — or type this report
              free-hand below.
            </>
          ) : templates.length ? (
            <>
              No <strong>{order?.modality}</strong> template exists yet. Author one under{" "}
              <strong>Report Templates</strong> — or type this report free-hand below.
            </>
          ) : (
            <>
              No templates exist yet. Create one under <strong>Report Templates</strong> — or
              type this report free-hand below.
            </>
          )}
        </div>
      ) : null}

      {pending.length && !isFinal ? (
        <div
          style={{
            ...cardStyle,
            padding: "8px 14px",
            marginBottom: 12,
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            fontSize: 12,
            color: "#991B1B",
          }}
        >
          Unfilled placeholders: {pending.map((p) => `{{${p}}}`).join(", ")}
        </div>
      ) : null}

      <ClinicalEditor
        ref={editorRef}
        key={`${order?.orderNo}-${isFinal ? "ro" : "rw"}`}
        value={report.content_html}
        onChange={setContent}
        readOnly={isFinal}
        height={460}
        placeholder="Load a template above, or type the report directly…"
      />

      {isFinal ? (
        <div style={{ fontSize: 12, color: C.textSub, marginTop: 10 }}>
          Finalized {report.reported_at ? formatDateTime(report.reported_at) : ""} by{" "}
          {report.radiologist_name || "—"}. Use <strong>Amend report</strong> to correct it.
        </div>
      ) : null}

      {/* ── ABHA / ABDM linking ─────────────────────────────────── */}
      <div style={{ ...cardStyle, padding: 16, marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", gap: 10, maxWidth: 560 }}>
            <SvgIcon name="shield" size={18} color={C.primary} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                ABHA (ABDM) Linking
              </div>
              <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>
                Link this report to the patient's ABHA (ABDM) account to securely
                share and access health records.
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                  marginTop: 8,
                }}
              >
                {ABDM_FEATURES.map((f) => (
                  <div
                    key={f.label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 11,
                      color: C.textSub,
                    }}
                  >
                    <SvgIcon name={f.icon} size={13} color={C.success} />
                    {f.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {abdmLinked ? (
            <div style={{ textAlign: "right" }}>
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: 20,
                  background: "#ECFDF5",
                  color: "#15803D",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Linked
              </span>
              <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>
                {abdmTransactionId}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleLinkAbha}
              disabled={abdmLinking || !canLinkAbha}
              title={
                canLinkAbha
                  ? undefined
                  : "ABHA address not available for this report yet"
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 18px",
                borderRadius: 8,
                border: "none",
                background: C.primary,
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: abdmLinking || !canLinkAbha ? "not-allowed" : "pointer",
                opacity: abdmLinking || !canLinkAbha ? 0.6 : 1,
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              {abdmLinking ? "Linking…" : "Link with ABHA"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
